import { gql, useMutation, useQuery } from '@apollo/client'

import {
  ConsoleEventCollectionReceiveTimeEditQuery,
  ConsoleEventCollectionReceiveTimeEditQueryVariables,
  EventBoxFragmentDoc,
  EventCollectionBoxFragmentDoc,
  UpdateEventCollectionPickupTimeMutation,
  UpdateEventCollectionPickupTimeMutationVariables,
} from '@/clients/utoniq-core/schema'
import { useCurrentUser, useMessage, useRouter, useTranslation } from '@/core'
import { makeViewModel } from '@/core/architecture'
import { dayjs } from '@/lib/dayjs'

import { Action, State } from './template'

/**
 *
 */
export const useViewModel = makeViewModel<State, Action>(() => {
  const { user } = useCurrentUser()
  const { router, routerPages, generatePath } =
    useRouter<{ eventId: string; collectionId: string }>()
  const { t } = useTranslation('consoleEventCollectionReceiveTimeEdit')
  const { showError, showMessage } = useMessage()
  const { data, loading } = useQuery<
    ConsoleEventCollectionReceiveTimeEditQuery,
    ConsoleEventCollectionReceiveTimeEditQueryVariables
  >(QUERY, {
    variables: {
      eventId: router.query.eventId,
      collectionId: router.query.collectionId,
    },
  })

  const [updateEventCollectionPickupTime, { loading: submitting }] =
    useMutation<
      UpdateEventCollectionPickupTimeMutation,
      UpdateEventCollectionPickupTimeMutationVariables
    >(UPDATE_EVENT_COLLECTION_PICKUP_TIME)

  const eventCollection = data?.event.eventCollections.edges.find(
    ({ node }) => node.id === router.query.collectionId
  )?.node

  return {
    state: {
      days: (data?.event?.dateArray ?? []) as string[],
      eventCollection,
      loading,
      submitting,
      artistId: user?.uid ?? '',
      maxValidMinutes:
        dayjs(data?.event.endedAt).diff(data?.event.startedAt, 'minute') || 0,
    },
    actions: {
      async onSubmit(inputs) {
        // 入力値を {date, options}[]の形式にコンバートし、時間・人数どちらも未入力なものを除外する
        const flatMappedPickupTime = inputs.pickupTimes
          .flatMap(item =>
            item.pickupTime.map(timeItem => ({
              options: timeItem.options,
              date: item.pickupDate,
            }))
          )
          .filter(
            item => !!item.options.time || !!item.options.availableUserNumber
          )

        // 入力値をチェックし、無効なものがあればエラーを表示してreturn
        if (
          flatMappedPickupTime.some(item => {
            return (
              !item.options.time.match(/^([01][0-9]|2[0-3]):([0-5][0-9])$/) ||
              !item.options.availableUserNumber ||
              item.options.availableUserNumber <= 0
            )
          })
        ) {
          showError(t('submitError'))
          return
        }

        // 日付順にソートし、ミューテーションのvariablesの形に合うように形成する
        const pickupTime = flatMappedPickupTime
          .sort((a, b) => {
            const aTimestamp = new Date(`${a.date} ${a.options.time}`).getTime()
            const bTimestamp = new Date(`${b.date} ${b.options.time}`).getTime()
            return aTimestamp - bTimestamp
          })
          .map(item => ({
            options: {
              time: new Date(`${item.date} ${item.options.time}`),
              availableUserNumber: item.options.availableUserNumber,
            },
          }))

        await updateEventCollectionPickupTime({
          variables: {
            inputs: {
              validMinutes: Number(inputs.validMinutes),
              collectionId: router.query.collectionId,
              eventId: router.query.eventId,
              pickupTime,
              pickupLocation: inputs.pickupLocation,
              purchaseLimitPerPickup: inputs.purchaseLimitPerPickup
                ? inputs.purchaseLimitPerPickup
                : undefined,
            },
          },
        }).then(() => {
          showMessage(t('submitSuccess'))
          router.goBack(
            generatePath(routerPages.ConsoleShokufesEventDetail, {
              eventId: router.query.eventId,
            })
          )
        })
      },
    },
  }
})

const QUERY = gql`
  query ConsoleEventCollectionReceiveTimeEdit(
    $eventId: ID!
    $collectionId: ID!
  ) {
    event(id: $eventId) {
      ...EventBox
    }
    collection(id: $collectionId) {
      id
      title
    }
  }
  ${EventBoxFragmentDoc}
`

const UPDATE_EVENT_COLLECTION_PICKUP_TIME = gql`
  mutation UpdateEventCollectionPickupTime(
    $inputs: UpdateEventCollectionPickupTimeInput!
  ) {
    updateEventCollectionPickupTime(inputs: $inputs) {
      ...EventCollectionBox
    }
  }
  ${EventCollectionBoxFragmentDoc}
`
